import { z } from "zod";
import { ClientPortalFeaturesSchema } from "./client-portal-features";
import { AboutImageInputSchema, normalizeAboutImage } from "./about-image";
import { StorefrontHexColorSchema } from "./storefront-color";

export const IntakeStatChartTypeSchema = z.enum([
  "pie",
  "donut",
  "bar",
  "line",
  "histogram",
]);

export const IntakeStatTimeBucketSchema = z.enum(["month", "quarter", "year"]);

export const IntakeStatDateModeSchema = z.enum(["occurrence", "age"]);

export const IntakeStatBinSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    label: z.string().min(1).max(80),
  })
  .refine((b) => b.min !== undefined || b.max !== undefined, {
    message: "Au moins une borne (min ou max) est requise",
  });

export const IntakeFieldStatisticsSchema = z.object({
  enabled: z.boolean(),
  chartType: IntakeStatChartTypeSchema.optional(),
  bins: z.array(IntakeStatBinSchema).max(20).optional(),
  timeBucket: IntakeStatTimeBucketSchema.optional(),
  dateMode: IntakeStatDateModeSchema.optional(),
});

export const IntakeFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: z.enum([
    "text",
    "email",
    "phone",
    "select",
    "textarea",
    "date",
    "number",
    "document",
  ]),
  required: z.boolean(),
  options: z.array(z.string()).optional(),
  statistics: IntakeFieldStatisticsSchema.optional(),
});

export const IntakeFormSchema = z.array(IntakeFieldSchema);

export const DiplomaSchema = z.object({
  label: z.string().min(1),
  imageUrl: z.string().nullable().optional(),
});

export const StorefrontThemeIdSchema = z.enum([
  "bleu",
  "ambre",
  "indigo",
  "verdure",
  "corail",
  "crepuscule",
  "blanc",
  "noir",
]);

export type StorefrontThemeId = z.infer<typeof StorefrontThemeIdSchema>;

export const StorefrontAdvantageSchema = z.object({
  title: z.string().min(1).max(120),
  text: z.string().max(500).optional(),
  /** Clé du registre d'icônes (logo) des avantages. */
  icon: z.string().max(40).optional(),
  /** Fond de la carte avantage. */
  bgColor: StorefrontHexColorSchema,
  /** Fond du badge logo. */
  iconBgColor: StorefrontHexColorSchema,
  /** Couleur du logo (icône). */
  iconColor: StorefrontHexColorSchema,
  /** Couleur du texte (titre + description). */
  textColor: StorefrontHexColorSchema,
});

export type StorefrontAdvantageDto = z.infer<typeof StorefrontAdvantageSchema>;

export const StorefrontBioFontSizeSchema = z.enum(["sm", "base", "lg"]);

export type StorefrontBioFontSize = z.infer<typeof StorefrontBioFontSizeSchema>;

/** Position de la photo de profil dans l'en-tête de la vitrine (`LEFT` = défaut). */
export const StorefrontProfileLayoutSchema = z.enum([
  "LEFT",
  "CENTER",
  "RIGHT",
]);

export type StorefrontProfileLayout = z.infer<
  typeof StorefrontProfileLayoutSchema
>;

export const OrganizationSchema = z.object({
  name: z.string().min(2),
  slug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/),
  timezone: z.string().optional(),
  /** Tampon entre deux RDV (minutes). */
  bufferMinutes: z.number().int().min(0).max(480).optional(),
  registrationMinLeadMinutes: z.number().int().min(0).optional(),
  registrationMaxAdvanceMinutes: z.number().int().min(-1).optional(), // -1 means unlimited
  siret: z.string().optional(),
  rpps: z.string().max(32).nullable().optional(),
  rcpMediatorInfo: z.string().max(2000).nullable().optional(),
  tva: z.string().optional(),
  address: z.string().optional(),
  /** Téléphone public affiché dans le pied de page de la vitrine. */
  contactPhone: z.string().max(40).nullable().optional(),
  /** Email public affiché dans le pied de page de la vitrine. */
  contactEmail: z.string().max(160).nullable().optional(),
  logo: z.string().nullable().optional(),
  profilePhoto: z.string().nullable().optional(),
  /** Recadrage de la photo de profil (valeur CSS object-position, ex. "50% 30%"). */
  profilePhotoPosition: z.string().max(20).nullable().optional(),
  bio: z.string().max(500).nullable().optional(),
  /** Galerie « À propos » : 4 images max, légende optionnelle par image. */
  aboutImages: z
    .array(AboutImageInputSchema)
    .max(4)
    .nullable()
    .optional()
    .transform((items) => {
      if (items == null) return items;
      return items
        .map(normalizeAboutImage)
        .filter((item): item is NonNullable<typeof item> => item != null);
    }),
  diplomas: z.array(DiplomaSchema).nullable().optional(),
  /** Bannière de couverture de la page de marque (data URL en entrée, URL média en sortie). */
  coverImage: z.string().nullable().optional(),
  /** Active la page de marque publique. */
  storefrontEnabled: z.boolean().optional(),
  /** Accroche affichée sous le nom sur la vitrine. */
  storefrontTagline: z.string().max(280).nullable().optional(),
  /** Phrase d'appel à l'action en fin de page (bouton « Rejoins-nous »). */
  storefrontCtaText: z.string().max(500).nullable().optional(),
  /** Fond de la cellule d'accroche de fin de page. */
  storefrontCtaBgColor: StorefrontHexColorSchema.nullable().optional(),
  /** Couleur du texte de l'accroche de fin de page. */
  storefrontCtaTextColor: StorefrontHexColorSchema.nullable().optional(),
  /** Image optionnelle de l'accroche de fin de page. */
  storefrontCtaImage: z.string().nullable().optional(),
  /** Couleur du texte de la biographie sur la vitrine. */
  storefrontBioTextColor: StorefrontHexColorSchema.nullable().optional(),
  /** Taille du texte de la biographie (`sm`, `base`, `lg`). */
  storefrontBioFontSize: StorefrontBioFontSizeSchema.nullable().optional(),
  /** Avantages éditables affichés sur la vitrine. */
  storefrontAdvantages: z
    .array(StorefrontAdvantageSchema)
    .max(12)
    .nullable()
    .optional(),
  /** Palette de couleurs de la page de marque publique (`bleu` = défaut). */
  storefrontTheme: StorefrontThemeIdSchema.nullable().optional(),
  /** Couleur de fond personnalisée de la vitrine (hex). */
  storefrontBackgroundColor: StorefrontHexColorSchema.nullable().optional(),
  /** Position de la photo de profil dans l'en-tête de la vitrine (`LEFT` = défaut). */
  storefrontProfileLayout: StorefrontProfileLayoutSchema.nullable().optional(),
  /** Couleur d'accent personnalisée de la vitrine (hex), prime sur les 8 thèmes prédéfinis. */
  storefrontAccentColor: StorefrontHexColorSchema.nullable().optional(),
  invoiceNumberTemplate: z.string().max(100).nullable().optional(),
  invoiceNumberSeqWidth: z.number().int().min(1).max(12).optional(),
  invoiceNumberSeqStart: z.number().int().min(1).max(999999).optional(),
  creditNoteNumberTemplate: z.string().max(100).nullable().optional(),
  creditNoteSeqWidth: z.number().int().min(1).max(12).optional(),
  creditNoteSeqStart: z.number().int().min(1).max(999999).optional(),
  clientPortalFeatures: ClientPortalFeaturesSchema.nullable().optional(),
  /** Toggle des notes client par séance dans les programmes (true = autorisé). */
  clientProgramSessionNotesEnabled: z.boolean().optional(),
  /** Professionnel de santé au sens réglementaire (avertissement dictée IA). */
  isHealthProfessional: z.boolean().optional(),
  /** Dictées vocales avec traitement IA Mistral (false = désactivé pour l'organisation). */
  voiceReportsEnabled: z.boolean().optional(),
  /** Texte affiché aux clients (politique d’annulation / modification). */
  clientCancellationPolicyText: z.string().max(8000).nullable().optional(),
  /** Minutes minimum avant le RDV pour annuler ou reprogrammer (0 = pas de restriction). */
  clientCancellationMinLeadMinutes: z.number().int().min(0).max(10080).optional(),
});

export type OrganizationDto = z.infer<typeof OrganizationSchema>;
